/* APPLYING A MOVE — the one impure half of the feature.
   core/moves.js decides what a move IS; this writes it onto an object and
   tells the rest of the app. Nothing else in the codebase needs to know the
   feature exists: what lands is ordinary keyframes on ordinary tracks.

   Deliberately not part of state/timeline.js. That file owns the playhead and
   the per-property track editors — the small operations everything else is
   built from. This is one composite action with its own sequencing, and the
   two are easier to read apart than together.

   NO LICENCE CHECK HERE. The Motion gate lives in the UI, the same posture as
   stopwatch() in ui/timeline-view.js, so the data layer stays callable from
   the test suite and from anything batched on top of it later. */

import { AXIS_TRACKS, animatableProps, copyValue, evalTrack, setKey } from '../core/anim.js';
import { buildMoveKeys, minLength } from '../core/moves.js';
import { clamp } from '../core/util.js';
import { applyLook, applyReveal, syncTransform } from '../scene/objects.js';
import { emit, on, requestRender } from './bus.js';
import { pushHistory } from './history.js';
import { S } from './store.js';
import { applyAnimation, frameDur, quantise, removeTrack } from './timeline.js';

/* The ceiling the dock's own Duration field enforces, and the practical limit
   on what the exporter can finish. Extending past it is refused out loud
   rather than clamped, because a move silently squashed into the last tenth
   of a second is not the move that was asked for. */
const MAX_DURATION = 30;

/** What a move's distances are relative to: the short side of the screen. */
const unit = () => Math.max(0.05, Math.min(S.screen?.w || 1, S.screen?.h || 1));

/* The same well-formedness normalizeAnim applies on load. Checked BEFORE
   writing rather than after, because a NaN in a keyframe is invisible until
   the project is reopened, at which point the key is silently dropped and the
   animation quietly changes shape. */
const sane = v => {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'boolean') return true;
  if (typeof v === 'string') return /^#[0-9a-f]{6}$/i.test(v);
  return !!v && typeof v === 'object' && ['x', 'y', 'z'].every(k => Number.isFinite(v[k]));
};

/**
 * Write `move` onto `o`.
 *
 * @param {object} opts.length  seconds, for a one-shot; ignored by a loop
 * @param {string} opts.start   'playhead' (default) or 'zero'
 * @returns {{ok:boolean, reason?:string, props?:string[], from?:number,
 *            to?:number, keys?:number, extended?:boolean}}
 */
export function applyMove(o, move, { length, start = 'playhead' } = {}) {
  if (!o || !move) return { ok: false, reason: 'Nothing to animate.' };

  /* animatableProps is the authority, not ANIMATABLE: it is what knows that a
     facing object's rotation is rewritten every frame and that an inset is
     fixed to the screen. */
  const legal = animatableProps(o);
  const missing = move.needs.filter(p => !legal.includes(p));
  if (missing.length)
    return { ok: false, reason: `“${move.name}” animates ${missing.join(' and ')}, which this object cannot.` };
  const refused = move.refuse && move.refuse(o);
  if (refused) return { ok: false, reason: refused };

  /* The pose FIRST. applyAnimation writes evaluated values back into o.pos and
     friends, so these are both the input and the output of the evaluator —
     read them before a single track changes. Where a property is already
     animated this is where the playhead left it, which is what the user can
     see, and matches how addTrack takes its first key. */
  const base = {
    pos: copyValue(o.pos), rot: copyValue(o.rot), scale: copyValue(o.scale),
    opacity: o.opacity, color: o.color,
    intensity: o.params?.intensity, visible: o.visible,
    /* Undefined on everything but a Text Area, and on one that has never been
       revealed — which revealOf reads as "all of it", so restoring undefined
       after a preview is the right answer rather than a gap. */
    reveal: o.params?.reveal, erase: o.params?.erase
  };

  const loop = move.span === 'duration';
  const span = loop
    ? S.timeline.duration
    : clamp(length == null ? move.length : length, minLength(move, frameDur()), MAX_DURATION);
  const at = loop || start === 'zero' ? 0 : S.playhead;   // the playhead is already on a frame
  const end = at + span;

  if (end > S.timeline.duration + 1e-9) {
    if (end > MAX_DURATION + 1e-9)
      return { ok: false, reason: `That would run past ${MAX_DURATION} seconds, which is as long as the timeline goes. `
                                + 'Move the playhead back, or shorten the move.' };
    /* Lengthen rather than write keyframes past the end: nothing clamps a key
       to the timeline, and one written beyond it can never be reached by the
       playhead and never appears in the export. The duration is part of the
       undo snapshot, so this comes back with Ctrl+Z like everything else. */
    S.timeline.duration = Math.min(MAX_DURATION, Math.ceil(end * S.timeline.fps) / S.timeline.fps);
  }

  const built = buildMoveKeys(move, base, {
    length: span, span, duration: S.timeline.duration, fps: S.timeline.fps, unit: unit()
  });
  for (const [prop, keys] of Object.entries(built))
    for (const k of keys)
      if (!sane(k.v)) return { ok: false, reason: `“${move.name}” could not be worked out for this object.` };

  /* Only the tracks this move owns. Every other track is left exactly as it
     was — that is the promise the panel makes, and the one worth testing.
     delete rather than removeTrack, which would run applyAnimation and emit
     once per property; this action gets one of each, at the end. */
  if (!o.anim) o.anim = {};
  /* A move owns the WHOLE property: if its vector was separated into X, Y and Z
     tracks those go too, or they would override the move's own track every frame. */
  for (const prop of move.needs) { delete o.anim[prop]; for (const p of AXIS_TRACKS[prop] || []) delete o.anim[p]; }

  let written = 0;
  for (const [prop, keys] of Object.entries(built)) {
    for (const k of keys) {
      /* ease ALWAYS explicit: setKey inherits the ease of a key it replaces,
         and defaults to 'inout' rather than linear otherwise. A boolean track
         is a hold track by definition — normalizeAnim rewrites it on load, so
         writing anything else would mean the file and the session disagree. */
      const ease = typeof k.v === 'boolean' ? 'hold' : k.ease;
      o.anim[prop] = setKey(o.anim[prop], quantise(at + k.t), k.v, ease);
    }
    written += o.anim[prop].length;
  }

  applyAnimation();
  pushHistory();
  emit('anim:changed', o);
  return { ok: true, props: Object.keys(built), from: at, to: quantise(end),
           keys: written, extended: end > S.timeline.duration - 1e-9 && !loop };
}

/* ── PREVIEW ────────────────────────────────────────────────────────────────
   The same motion, played into the viewport, writing nothing.

   Without this, seeing what a move does means applying it, watching, and
   undoing — three actions and a history entry to answer "is this the one?".
   The preview runs the identical keys applyMove would write, evaluated by the
   identical evaluator, so what you watch is what you would get.

   It deliberately does NOT touch o.anim. Writing temporary tracks would be
   less code, but a save or a history push landing mid-preview would then
   capture keyframes nobody asked for. Values are written straight onto the
   object and put back when it stops. */

let preview = null;                       // { o, props, base, raf, onEnd }

/** Mirrors assign() in timeline.js: vectors are mutated IN PLACE, because the
 *  inspector and the gizmo hold live references to o.pos and friends. */
function put(o, prop, v) {
  if (prop === 'intensity') { o.params.intensity = v; return; }
  if (prop === 'reveal') { o.params.reveal = v; return; }
  if (prop === 'erase') { o.params.erase = v; return; }
  const cur = o[prop];
  if (cur && typeof cur === 'object' && v && typeof v === 'object') {
    cur.x = v.x; cur.y = v.y; cur.z = v.z; return;
  }
  o[prop] = v;
}

export const isPreviewing = () => !!preview;

/** Which object a running preview belongs to, so a caller can tell whether
 *  the one on screen is still the one being previewed. */
export const previewingObject = () => (preview ? preview.o : null);

/** Put the object back exactly as it was, and let any real tracks reassert. */
export function stopPreview() {
  if (!preview) return;
  const { o, props, base, raf, onEnd } = preview;
  preview = null;
  cancelAnimationFrame(raf);
  for (const prop of props) put(o, prop, base[prop]);
  syncTransform(o);
  applyLook(o);
  applyReveal(o);
  /* Whatever the document actually says wins the moment the preview lets go —
     an object with real tracks returns to its keyframed pose, not to the pose
     the preview happened to capture. */
  applyAnimation();
  requestRender();
  if (onEnd) onEnd();
}

/**
 * Play `move` on `o` without writing anything. Returns {ok}.
 * A loop previews one cycle, capped so a 30-second timeline is not a
 * 30-second wait.
 */
export function previewMove(o, move, { length, onEnd } = {}) {
  stopPreview();
  if (!o || !move) return { ok: false };
  const legal = animatableProps(o);
  if (move.needs.some(p => !legal.includes(p))) return { ok: false };

  const base = {
    pos: copyValue(o.pos), rot: copyValue(o.rot), scale: copyValue(o.scale),
    opacity: o.opacity, color: o.color,
    intensity: o.params?.intensity, visible: o.visible,
    /* Undefined on everything but a Text Area, and on one that has never been
       revealed — which revealOf reads as "all of it", so restoring undefined
       after a preview is the right answer rather than a gap. */
    reveal: o.params?.reveal, erase: o.params?.erase
  };
  const loop = move.span === 'duration';
  const span = loop
    ? Math.min(S.timeline.duration, 4)
    : clamp(length == null ? move.length : length, minLength(move, frameDur()), MAX_DURATION);
  const built = buildMoveKeys(move, base, {
    length: span, span, duration: S.timeline.duration, fps: S.timeline.fps, unit: unit()
  });
  const props = Object.keys(built);
  const started = performance.now();

  const frame = () => {
    if (!preview) return;
    const t = (performance.now() - started) / 1000;
    for (const prop of props) {
      const v = evalTrack(built[prop], Math.min(t, span));
      if (v !== undefined) put(o, prop, v);
    }
    syncTransform(o);
    applyLook(o);
    applyReveal(o);          // a no-op on everything that is not a Text Area
    requestRender();
    if (t >= span) { stopPreview(); return; }
    preview.raf = requestAnimationFrame(frame);
  };

  preview = { o, props, base, raf: requestAnimationFrame(frame), onEnd };
  return { ok: true, span };
}

/* Anything that changes what is on screen ends the preview, rather than
   leaving a half-played pose behind on an object nobody is looking at any
   more. Scrubbing counts: the playhead is the document's own answer to where
   things are.

   These three are events that actually fire. Checked, because the bus header
   lists 'sel:changed', 'scene:changed', 'params:changed', 'view:changed',
   'solve:changed' and 'history:changed' as "in use" and NOTHING emits any of
   them — selection calls renderRails() directly instead (scene/objects.js's
   select). ui/timeline-view.js still subscribes to 'sel:changed' and has been
   quietly dead ever since. Selection and stage changes are therefore stopped
   at their call sites, where a line of code is worth more than a subscription
   that reads correctly and never runs. */
on('playhead:moved', stopPreview);
on('playing:changed', stopPreview);
on('project:changed', stopPreview);

/** Stop animating the properties this move owns, and nothing else. */
export function clearMoveTracks(o, move) {
  if (!o || !move || !o.anim) return { ok: false, props: [] };
  const gone = move.needs.flatMap(p => [p, ...(AXIS_TRACKS[p] || [])]).filter(p => o.anim[p]);
  for (const p of gone) removeTrack(o, p);       // deletes o.anim with the last track
  if (gone.length) pushHistory();
  return { ok: !!gone.length, props: gone };
}

/**
 * Stop animating this object entirely — every track, whatever put it there.
 *
 * The blunt instrument next to clearMoveTracks, and the way back from a scene
 * that has been experimented on: tracks accumulate one property at a time and
 * there was no way to see the object still again short of deleting it.
 * Undoable like everything else, which is why it asks nothing first.
 */
export function clearAllTracks(o) {
  if (!o || !o.anim) return { ok: false, props: [], keys: 0 };
  const props = Object.keys(o.anim);
  const keys = props.reduce((n, p) => n + o.anim[p].length, 0);
  for (const p of props) removeTrack(o, p);   // takes o.anim with the last one
  pushHistory();
  return { ok: true, props, keys };
}

/** Does this object already carry every track the move writes? */
export const moveIsOn = (o, move) => !!o?.anim && move.needs.every(p => !!o.anim[p]);
